import { getLogger } from 'src/utils/datadog-logger';
import type { EmbeddedOAuthSenderKind } from 'src/oauth/embedded-oauth-sender-kind';

const NONCE_STORAGE_KEY = 'dutchie:oauthFlowNonces';
const NONCE_TTL_MS = 5 * 60 * 1000;
const MAX_OUTSTANDING_NONCES = 5;

export type OAuthNonceOutcome = 'expired' | 'match' | 'mismatch' | 'missing';
export type OAuthProvider = 'apple' | 'google' | 'unknown';

type Nonces = Record<string, number>;

const readNonces = (): Nonces => {
  const stored = window.localStorage.getItem(NONCE_STORAGE_KEY);
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored ?? '{}');
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(parsed).filter(
      ([nonce, timestamp]) =>
        /^[A-Za-z0-9_-]{16,64}$/.test(nonce) && typeof timestamp === 'number' && Number.isFinite(timestamp)
    )
  );
};

export const createOAuthFlowNonce = (): string | null => {
  try {
    const bytes = window.crypto.getRandomValues(new Uint8Array(24));
    const nonce = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    const now = Date.now();
    const unexpired = Object.entries(readNonces())
      .filter(([, createdAt]) => createdAt <= now && now - createdAt <= NONCE_TTL_MS)
      .sort(([, first], [, second]) => first - second)
      .slice(-(MAX_OUTSTANDING_NONCES - 1));

    window.localStorage.setItem(NONCE_STORAGE_KEY, JSON.stringify(Object.fromEntries([...unexpired, [nonce, now]])));
    return nonce;
  } catch {
    return null;
  }
};

export const consumeOAuthFlowNonce = (nonce: string[] | string | null | undefined): OAuthNonceOutcome => {
  if (typeof nonce !== 'string' || !nonce) {
    return 'missing';
  }

  try {
    const nonces = readNonces();
    if (!Object.prototype.hasOwnProperty.call(nonces, nonce)) {
      return 'mismatch';
    }
    const createdAt = nonces[nonce];
    if (createdAt > Date.now() || Date.now() - createdAt > NONCE_TTL_MS) {
      return 'expired';
    }

    const remaining = Object.fromEntries(Object.entries(nonces).filter(([candidate]) => candidate !== nonce));
    window.localStorage.setItem(NONCE_STORAGE_KEY, JSON.stringify(remaining));
    return 'match';
  } catch {
    return 'mismatch';
  }
};

export const stripOAuthCallbackTokens = (): void => {
  const callbackUrl = new URL(window.location.href);
  callbackUrl.searchParams.delete('token');
  callbackUrl.searchParams.delete('oauth_nonce');
  window.history.replaceState(
    window.history.state,
    '',
    `${callbackUrl.pathname}${callbackUrl.search}${callbackUrl.hash}`
  );
};

type RedirectHostKind = 'custom' | 'dutchie' | 'none';
type CallbackQuery = { redirectUrl: string[] | string | undefined; embeddedPopup: string[] | string | undefined };

const redirectHostKind = (value: string): RedirectHostKind => {
  try {
    const url = new URL(value, window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return 'none';
    const host = url.hostname.toLowerCase().replace(/\.$/, '');
    return host === 'dutchie.com' || host.endsWith('.dutchie.com') ? 'dutchie' : 'custom';
  } catch {
    return 'none';
  }
};

const logger = getLogger('oauth-nonce');

export const reportOAuthNonceOutcome = (
  outcome: OAuthNonceOutcome,
  provider: OAuthProvider,
  route: string,
  { redirectUrl, embeddedPopup }: CallbackQuery
): void => {
  const redirect = Array.isArray(redirectUrl) ? redirectUrl[redirectUrl.length - 1] : redirectUrl;
  const hasRedirectUrl = typeof redirect === 'string' && redirect.trim().length > 0;
  const popup = Array.isArray(embeddedPopup) ? embeddedPopup[embeddedPopup.length - 1] : embeddedPopup;
  logger.info('OAuth callback nonce outcome', {
    outcome,
    oauth_provider: provider,
    route,
    has_redirect_url: hasRedirectUrl,
    redirect_host_kind: hasRedirectUrl ? redirectHostKind(redirect) : 'none',
    is_embedded_popup: popup === 'true',
  });
};

export const reportEmbeddedOAuthSender = (
  sameOrigin: boolean,
  isPopupSource: boolean,
  senderKind: EmbeddedOAuthSenderKind,
  trackedPopupCount: number
): void => {
  logger.info('Embedded OAuth sender', {
    same_origin: sameOrigin,
    is_popup_source: isPopupSource,
    sender_kind: senderKind,
    tracked_popup_count: Math.min(5, Math.max(0, trackedPopupCount)),
  });
};
